import React, { useState, useMemo, useEffect } from "react";
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import { TrashIcon, PlusIcon, XMarkIcon, PencilIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, StarIcon, ListBulletIcon, FolderIcon, EllipsisVerticalIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import Select from "react-select";

const SoinsCatalog = ({ soins, groups, onSoinsChange, onGroupsChange, onExport, onImport, onReset }) => {
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newGroup, setNewGroup] = useState([]);
  const [error, setError] = useState("");
  const [searchName, setSearchName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [draggedGroup, setDraggedGroup] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ code: "", name: "", price: "", groups: [] });
  const [selectedRows, setSelectedRows] = useState({});
  const [deleteBulkConfirm, setDeleteBulkConfirm] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [editGroupModal, setEditGroupModal] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");

  const handleSelectAll = (checked) => {
    if (checked) {
      const allSelected = {};
      filteredSoins.forEach((_, idx) => {
        allSelected[idx] = true;
      });
      setSelectedRows(allSelected);
    } else {
      setSelectedRows({});
    }
  };

  const toggleRowSelect = (index) => {
    setSelectedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleDeleteBulk = () => {
    const selectedIndices = Object.keys(selectedRows).filter(k => selectedRows[k]).map(Number).sort((a, b) => b - a);
    const updated = soins.filter((_, i) => !selectedIndices.includes(soins.indexOf(filteredSoins[i])));
    onSoinsChange(updated);
    setSelectedRows({});
    setDeleteBulkConfirm(false);
  };

  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  // Ensure Bookmarks group exists and is first
  useEffect(() => {
    if (!groups.includes("Bookmarks")) {
      onGroupsChange(["Bookmarks", ...groups]);
    }
  }, [groups, onGroupsChange]);

  const toggleBookmark = (index) => {
    const soin = soins[index];
    const soinGroups = Array.isArray(soin.groups) ? soin.groups : (soin.group ? [soin.group] : []);
    const isBookmarked = soinGroups.includes("Bookmarks");
    
    const updated = soins.map((s, i) => {
      if (i !== index) return s;
      if (isBookmarked) {
        return { ...s, groups: soinGroups.filter(g => g !== "Bookmarks") };
      } else {
        return { ...s, groups: [...soinGroups, "Bookmarks"] };
      }
    });
    onSoinsChange(updated);
  };

  const handleAddGroup = (e) => {
    e.preventDefault();
    const trimmed = newGroupName.trim();
    if (!trimmed) return;
    if (groups.includes(trimmed)) {
      alert("Ce groupe existe déjà.");
      return;
    }
    onGroupsChange([...groups, trimmed]);
    setNewGroupName("");
    setIsGroupModalOpen(false);
  };

  const handleDeleteGroup = (groupToDelete) => {
    const used = soins.some(s => {
      const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
      return soinGroups.includes(groupToDelete);
    });
    if (used) {
      setDeleteGroupConfirm(groupToDelete);
      return;
    }
    onGroupsChange(groups.filter(g => g !== groupToDelete));
  };

  const confirmDeleteGroup = () => {
    if (deleteGroupConfirm) {
      const updated = soins.map(s => {
        const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
        if (soinGroups.includes(deleteGroupConfirm)) {
          return { ...s, groups: soinGroups.filter(g => g !== deleteGroupConfirm) };
        }
        return s;
      });
      onSoinsChange(updated);
      onGroupsChange(groups.filter(g => g !== deleteGroupConfirm));
      setDeleteGroupConfirm(null);
    }
  };

  const openEditGroupModal = (groupName) => {
    setEditGroupModal(groupName);
    setEditGroupName(groupName);
  };

  const closeEditGroupModal = () => {
    setEditGroupModal(null);
    setEditGroupName("");
  };

  const handleSaveEditGroup = () => {
    const trimmed = editGroupName.trim();
    if (!trimmed || trimmed === editGroupModal) {
      closeEditGroupModal();
      return;
    }

    // Check if new name already exists
    if (groups.includes(trimmed)) {
      setToast({ message: "Ce groupe existe déjà", type: "error" });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    // Update group name in groups array
    const updatedGroups = groups.map(g => g === editGroupModal ? trimmed : g);
    onGroupsChange(updatedGroups);

    // Update group name in all soins
    const updatedSoins = soins.map(s => {
      const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
      if (soinGroups.includes(editGroupModal)) {
        return {
          ...s,
          groups: soinGroups.map(g => g === editGroupModal ? trimmed : g)
        };
      }
      return s;
    });
    onSoinsChange(updatedSoins);

    setToast({ message: `Groupe renommé en "${trimmed}"`, type: "success" });
    setTimeout(() => setToast(null), 3000);

    closeEditGroupModal();
  };

  const handleDragStart = (e, group) => {
    setDraggedGroup(group);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (group) => {
    if (draggedGroup !== group) {
      setDragOverGroup(group);
    }
  };

  const handleDragLeave = () => {
    setDragOverGroup(null);
  };

  const handleDrop = (e, targetGroup) => {
    e.preventDefault();
    setDragOverGroup(null);
    
    if (!draggedGroup || draggedGroup === targetGroup) {
      setDraggedGroup(null);
      return;
    }

    const draggedIndex = groups.indexOf(draggedGroup);
    const targetIndex = groups.indexOf(targetGroup);
    
    if (draggedIndex > -1 && targetIndex > -1) {
      const newGroups = [...groups];
      newGroups.splice(draggedIndex, 1);
      newGroups.splice(targetIndex, 0, draggedGroup);
      onGroupsChange(newGroups);
    }
    setDraggedGroup(null);
  };

  const handleDragEnd = () => {
    setDraggedGroup(null);
    setDragOverGroup(null);
  };

  const openEditModal = (index, soin) => {
    setEditModal(index);
    const soinGroups = Array.isArray(soin.groups) ? soin.groups : (soin.group ? [soin.group] : []);
    setEditForm({
      code: soin.code,
      name: soin.name,
      price: soin.defaultPrice,
      groups: soinGroups,
    });
  };

  const closeEditModal = () => {
    setEditModal(null);
    setEditForm({ code: "", name: "", price: "", groups: [] });
  };

  const handleSaveEdit = () => {
    if (!editForm.code.trim() || !editForm.name.trim() || !editForm.price) {
      alert("Tous les champs sont obligatoires.");
      return;
    }

    const price = Number(editForm.price);
    if (Number.isNaN(price) || price <= 0) {
      alert("Le prix doit être un nombre positif.");
      return;
    }

    const updated = soins.map((s, i) =>
      i === editModal
        ? { ...s, code: editForm.code.trim(), name: editForm.name.trim(), defaultPrice: price, groups: editForm.groups }
        : s
    );
    onSoinsChange(updated);
    closeEditModal();
  };

  const handleDelete = (index) => {
    const updated = soins.filter((_, i) => i !== index);
    onSoinsChange(updated);
    setDeleteConfirm(null);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    setError("");

    if (!newName.trim() || !newPrice) {
      setError("Veuillez renseigner le nom et le prix.");
      return;
    }

    const price = Number(newPrice);
    if (Number.isNaN(price) || price <= 0) {
      setError("Le prix doit être un nombre positif.");
      return;
    }

    const code = newCode.trim() || "-";
    const exists = newCode.trim() && soins.some(
      (s) => s.code.toLowerCase() === code.toLowerCase()
    );
    if (exists) {
      setError("Un soin avec ce code existe déjà.");
      return;
    }

    const updated = [
      ...soins,
      {
        code: code,
        name: newName.trim(),
        defaultPrice: price,
        groups: Array.isArray(newGroup) ? newGroup : [],
      },
    ];
    onSoinsChange(updated);
    
    // Show success toast
    setToast({ message: `"${newName.trim()}" ajouté avec succès!`, type: "success" });
    setTimeout(() => setToast(null), 3000);
    
    // Clear form
    setNewCode("");
    setNewName("");
    setNewPrice("");
    setNewGroup([]);
  };

  const normalize = (str) =>
    (str ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredSoins = useMemo(() => {
    return soins.filter((s) => {
      const q = normalize(searchName.trim());
      if (!q) return true;
      const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
      return (
        normalize(s.name).includes(q) ||
        normalize(s.code).includes(q) ||
        soinGroups.some(g => normalize(g).includes(q))
      );
    });
  }, [soins, searchName]);

  // Memoized sorted list
  const sortedFilteredSoins = useMemo(() => {
    let sorted = [...filteredSoins];
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      sorted.sort((a, b) => {
        let aVal, bVal;
        if (id === 'code') {
          aVal = a.code;
          bVal = b.code;
        } else if (id === 'name') {
          aVal = a.name;
          bVal = b.name;
        } else if (id === 'groups') {
          aVal = (Array.isArray(a.groups) ? a.groups : (a.group ? [a.group] : [])).join(', ');
          bVal = (Array.isArray(b.groups) ? b.groups : (b.group ? [b.group] : [])).join(', ');
        } else if (id === 'price') {
          aVal = a.defaultPrice;
          bVal = b.defaultPrice;
        }
        if (typeof aVal === 'string') {
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        return desc ? bVal - aVal : aVal - bVal;
      });
    }
    return sorted;
  }, [filteredSoins, sorting]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Catalogue des soins</h1>
        <p className="text-blue-100">Gérez vos services dentaires, groupes et tarification</p>
      </div>

      {/* Action Buttons */}
      <div className="admin-controls flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg p-4 shadow-md border border-gray-100">
        <div className="flex gap-2">
          <button
            onClick={onExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-200 border border-blue-200"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Exporter
          </button>
          <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-200 cursor-pointer border border-blue-200">
            <ArrowUpTrayIcon className="w-4 h-4" />
            Importer
            <input
              type="file"
              accept=".json"
              onChange={onImport}
              className="hidden"
            />
          </label>
        </div>
        <button
          onClick={() => setShowResetModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-all duration-200 border border-orange-200"
          title="Réinitialiser avec les valeurs par défaut"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Réinitialiser
        </button>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* LEFT COLUMN - Groups Management */}
        {groups.length > 0 && (
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-md sticky top-6 max-h-[calc(100vh-120px)] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FolderIcon className="w-5 h-5 text-blue-600" />
                    Groupes
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">({groups.length} au total)</p>
                </div>
                <button
                  onClick={() => setIsGroupModalOpen(true)}
                  className="inline-flex items-center justify-center w-9 h-9 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-110 shadow-md hover:shadow-lg flex-shrink-0"
                  title="Ajouter un groupe"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {groups.map((group) => {
                  const count = soins.filter(s => {
                    const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
                    return soinGroups.includes(group);
                  }).length;
                  const isDragged = draggedGroup === group;
                  const isDragOver = dragOverGroup === group;
                  
                  return (
                    <div
                      key={group}
                      draggable
                      onDragStart={(e) => handleDragStart(e, group)}
                      onDragOver={handleDragOver}
                      onDragEnter={() => handleDragEnter(group)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, group)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 px-3 py-2.5 bg-white border-2 rounded-lg text-sm text-gray-800 transition-all duration-200 group/drag ${
                        isDragged
                          ? 'opacity-40 border-gray-300 bg-gray-50 shadow-md'
                          : isDragOver
                          ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                          : 'border-blue-300 hover:shadow-md hover:border-blue-400 cursor-move'
                      }`}
                    >
                      <EllipsisVerticalIcon className={`w-4 h-4 flex-shrink-0 transition-all ${isDragged ? 'text-gray-300' : 'text-gray-400 group-hover/drag:text-blue-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate text-xs">{group}</p>
                        <p className="text-xs text-gray-500">{count} soin{count !== 1 ? 's' : ''}</p>
                      </div>
                      {group !== "Bookmarks" && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openEditGroupModal(group)}
                            className="text-gray-400 hover:text-blue-600 transition-colors hover:bg-blue-50 rounded-full p-1"
                            title="Éditer ce groupe"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group)}
                            className="text-gray-400 hover:text-red-600 transition-colors hover:bg-red-50 rounded-full p-1"
                            title="Supprimer ce groupe"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* RIGHT COLUMN - Soins Management */}
        <div className={groups.length > 0 ? "lg:col-span-3" : "lg:col-span-4"}>
          <div className="space-y-5">
            {/* Add Soin Form */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-gray-200 rounded-xl p-4 shadow-md">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-3 mb-4">
                <PlusIcon className="w-5 h-5 text-blue-600" />
                <span>Ajouter un soin</span>
                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  Total: {soins.length}
                </span>
              </h2>
              <form onSubmit={handleAdd} className="space-y-3">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">Code</label>
                    <input
                      type="text"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white font-mono"
                      placeholder="S001"
                    />
                  </div>
                  <div className="col-span-4">
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">Nom</label>
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                      placeholder="Ex: Détartrage"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">Groupes</label>
                    <Select
                      isMulti
                      options={groups.map(group => ({ value: group, label: group }))}
                      value={newGroup.map(g => ({ value: g, label: g }))}
                      onChange={(selected) => setNewGroup(selected ? selected.map(s => s.value) : [])}
                      placeholder="Sélectionner..."
                      className="text-sm"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderWidth: '2px',
                          borderColor: '#d1d5db',
                          minHeight: '36px',
                          padding: '0.125rem',
                          '&:focus-within': {
                            borderColor: '#3b82f6',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                          }
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '0.5rem 0.75rem',
                          backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e0e7ff' : 'white',
                          color: state.isSelected ? 'white' : '#1f2937'
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">Prix</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white font-mono"
                      placeholder="0"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">&nbsp;</label>
                    <button
                      type="submit"
                      className="w-full h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-center"
                    >
                      +
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                )}
              </form>
            </div>

            {/* Search Box */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-4 shadow-sm">
              <label className="block text-sm font-bold text-gray-700 mb-2">Rechercher</label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                placeholder="Chercher par code, nom ou groupe..."
              />
            </div>

            {/* Soins Table */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ListBulletIcon className="w-5 h-5 text-blue-600" />
                  Liste des soins
                  <span className="ml-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                    {filteredSoins.length}
                  </span>
                </h3>
                {selectedCount > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600">{selectedCount} sélectionné(s)</span>
                    <button
                      onClick={() => setDeleteBulkConfirm(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto rounded-xl border-2 border-gray-200 shadow-lg">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedCount === filteredSoins.length && filteredSoins.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 accent-blue-600"
                          />
                        </th>
                        <th className="px-5 py-4 text-left font-bold text-gray-800 whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => setSorting(s => s[0]?.id === 'code' ? [{ id: 'code', desc: !s[0]?.desc }] : [{ id: 'code', desc: false }])}>
                          <div className="flex items-center gap-2">
                            Code
                            {sorting[0]?.id === 'code' && (
                              sorting[0]?.desc ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />
                            )}
                            {sorting[0]?.id !== 'code' && <ChevronUpDownIcon className="w-4 h-4 opacity-50" />}
                          </div>
                        </th>
                        <th className="px-5 py-4 text-left font-bold text-gray-800 cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => setSorting(s => s[0]?.id === 'name' ? [{ id: 'name', desc: !s[0]?.desc }] : [{ id: 'name', desc: false }])}>
                          <div className="flex items-center gap-2">
                            Nom du soin
                            {sorting[0]?.id === 'name' && (
                              sorting[0]?.desc ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />
                            )}
                            {sorting[0]?.id !== 'name' && <ChevronUpDownIcon className="w-4 h-4 opacity-50" />}
                          </div>
                        </th>
                        <th className="px-2 py-3 text-left font-bold text-gray-800 whitespace-nowrap text-xs cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => setSorting(s => s[0]?.id === 'groups' ? [{ id: 'groups', desc: !s[0]?.desc }] : [{ id: 'groups', desc: false }])}>
                          <div className="flex items-center gap-2">
                            Groupe
                            {sorting[0]?.id === 'groups' && (
                              sorting[0]?.desc ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />
                            )}
                            {sorting[0]?.id !== 'groups' && <ChevronUpDownIcon className="w-4 h-4 opacity-50" />}
                          </div>
                        </th>
                        <th className="px-5 py-4 text-left font-bold text-gray-800 whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => setSorting(s => s[0]?.id === 'price' ? [{ id: 'price', desc: !s[0]?.desc }] : [{ id: 'price', desc: false }])}>
                          <div className="flex items-center gap-2">
                            Prix (DH)
                            {sorting[0]?.id === 'price' && (
                              sorting[0]?.desc ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />
                            )}
                            {sorting[0]?.id !== 'price' && <ChevronUpDownIcon className="w-4 h-4 opacity-50" />}
                          </div>
                        </th>
                        <th className="px-5 py-4 text-right font-bold text-gray-800 admin-controls">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {sortedFilteredSoins.map((soin) => {
                        const actualIdx = soins.findIndex(s => s.code === soin.code && s.name === soin.name && s.defaultPrice === soin.defaultPrice);
                        const isSelected = selectedRows[filteredSoins.findIndex(s => s === soin)] || false;
                        return (
                          <tr key={actualIdx} className={`transition-colors duration-150 border-b border-gray-100 ${isSelected ? 'bg-blue-50' : 'hover:bg-blue-50'}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRowSelect(filteredSoins.findIndex(s => s === soin))}
                                className="w-4 h-4 accent-blue-600"
                              />
                            </td>
                            <td className="px-5 py-4 text-gray-900 font-semibold whitespace-nowrap">
                              <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                                {soin.code}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-gray-800 font-medium">{soin.name}</td>
                            <td className="px-2 py-3">
                              {(() => {
                                const soinGroups = Array.isArray(soin.groups) ? soin.groups : (soin.group ? [soin.group] : []);
                                return soinGroups.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {soinGroups.map(g => (
                                      <span key={g} className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                                        {g}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                );
                              })()}
                            </td>
                            <td className="px-5 py-4"><span className="text-gray-900 font-semibold">{soin.defaultPrice} DH</span></td>
                            <td className="px-5 py-4 text-right admin-controls">
                              <div className="flex gap-2 justify-end">
                                {(() => {
                                  const soinGroups = Array.isArray(soin.groups) ? soin.groups : (soin.group ? [soin.group] : []);
                                  const isBookmarked = soinGroups.includes("Bookmarks");
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => toggleBookmark(actualIdx)}
                                      className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 transform hover:scale-110 ${
                                        isBookmarked
                                          ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100'
                                          : 'text-gray-300 hover:text-yellow-400 hover:bg-yellow-50'
                                      }`}
                                      title={isBookmarked ? "Retirer des favoris" : "Ajouter aux favoris"}
                                    >
                                      {isBookmarked ? <StarIconSolid className="w-4 h-4" /> : <StarIcon className="w-4 h-4" />}
                                    </button>
                                  );
                                })()}
                                <button
                                  type="button"
                                  onClick={() => openEditModal(actualIdx, soin)}
                                  className="inline-flex items-center justify-center w-9 h-9 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-all duration-200 transform hover:scale-110"
                                  title="Éditer ce soin"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteConfirm(actualIdx)}
                                  className="inline-flex items-center justify-center w-9 h-9 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all duration-200 transform hover:scale-110"
                                  title="Supprimer ce soin"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredSoins.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-5 py-8 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <div className="text-5xl mb-2">📋</div>
                              <p className="text-gray-600 font-medium">
                                {searchName ? "Aucun soin ne correspond à votre recherche." : "Aucun soin défini pour le moment."}
                              </p>
                              <p className="text-gray-500 text-sm mt-1">
                                {searchName ? "Essayez d'autres termes" : "Commencez par ajouter un soin"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Group Modal */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsGroupModalOpen(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200 mx-4">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white rounded-t-xl">
              <h3 className="text-xl font-bold">Ajouter un groupe</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nom du groupe</label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Ex: Consultations"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsGroupModalOpen(false)}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all"
                  >
                    Créer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {deleteGroupConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteGroupConfirm(null)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-2">Êtes-vous sûr de vouloir supprimer le groupe <span className="font-semibold text-gray-900">"{deleteGroupConfirm}"</span> ?</p>
            <p className="text-sm text-gray-500 mb-6">Ce groupe sera supprimé de tous les soins qui l'utilisent.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteGroupConfirm(null)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all">Annuler</button>
              <button type="button" onClick={confirmDeleteGroup} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Soin Confirmation Modal */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirm(null)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir supprimer ce soin ? Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all">Annuler</button>
              <button type="button" onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Bulk Confirmation Modal */}
      {deleteBulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteBulkConfirm(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir supprimer {selectedCount} soin(s) ? Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteBulkConfirm(false)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all">Annuler</button>
              <button type="button" onClick={handleDeleteBulk} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeEditModal}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-md border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Éditer un soin</h3>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Code</label>
                <input type="text" value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white font-mono" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nom du soin</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Groupes</label>
                <Select isMulti options={groups.map(g => ({ value: g, label: g }))} value={editForm.groups.map(g => ({ value: g, label: g }))} onChange={(selected) => setEditForm({ ...editForm, groups: selected ? selected.map(s => s.value) : [] })} placeholder="Sélectionner des groupes..." className="text-sm" styles={{ control: (base) => ({ ...base, borderWidth: '2px', borderColor: '#d1d5db', padding: '0.25rem', '&:focus-within': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' } }), option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e0e7ff' : 'white', color: state.isSelected ? 'white' : '#1f2937' }) }} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Prix (DH)</label>
                <input type="number" min="0" step="1" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white font-mono" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeEditModal} className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all">Annuler</button>
                <button type="submit" className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {editGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeEditGroupModal}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Éditer le groupe</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nom du groupe</label>
                <input
                  type="text"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Consultations"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4 mt-6">
              <button
                type="button"
                onClick={closeEditGroupModal}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSaveEditGroup}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowResetModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:w-full sm:max-w-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">⚠️ Réinitialiser l'application</h3>
            <p className="text-gray-600 mb-6">
              Cette action supprimera <strong>toutes les anciennes données</strong> et réinitialisera l'application avec les valeurs par défaut. Cette action est <strong>irréversible</strong>.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowResetModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowResetModal(false);
                  onReset();
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-50">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-white font-medium flex items-center gap-2 animate-fade-in ${
            toast.type === "success" 
              ? "bg-green-500 hover:bg-green-600" 
              : "bg-red-500 hover:bg-red-600"
          }`}>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default SoinsCatalog;